import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function EqualityDiversityInclusionPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Equality, Diversity & Inclusion Policy - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Equality, Diversity & Inclusion Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Our commitment to promoting equality, fairness and respect for all.",
    "url": `${window.location.origin}/equality-diversity-inclusion-policy`
  };

  return (
    <>
      <SEO
        title="Equality, Diversity & Inclusion Policy | LEWIS SECURITY North Wales"
        description="LEWIS SECURITY is committed to promoting equality, fairness and respect for all clients, learners, contractors and staff. Learn about our commitment to diversity and inclusion across all our services."
        keywords="equality policy, diversity, inclusion, anti-discrimination, protected characteristics, equal opportunities, fairness"
        schema={schema}
        canonical={`${window.location.origin}/equality-diversity-inclusion-policy`}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8 font-poppins">Equality, Diversity &amp; Inclusion Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> 9 December 2025
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Commitment</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lewis Security, Risk &amp; Compliance Solutions is committed to promoting equality, fairness and respect for all clients, learners, contractors and staff.
                </p>
                <div className="bg-teal-50 rounded-xl p-8 border border-teal-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Zero Tolerance</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We do not tolerate <strong>discrimination, harassment or victimisation</strong> in any form.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Protected Characteristics</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We do not tolerate discrimination, harassment or victimisation on the grounds of:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Age</h3>
                        <p className="text-sm text-gray-600">All age groups are treated with equal respect</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-wheelchair-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Disability</h3>
                        <p className="text-sm text-gray-600">Physical or mental impairments</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-heart-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Gender Reassignment</h3>
                        <p className="text-sm text-gray-600">Transitioning or transitioned gender identity</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-heart-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Marriage or Civil Partnership</h3>
                        <p className="text-sm text-gray-600">Marital or partnership status</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-parent-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Pregnancy and Maternity</h3>
                        <p className="text-sm text-gray-600">Pregnancy, childbirth and maternity leave</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-global-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Race, Nationality or Ethnic Origin</h3>
                        <p className="text-sm text-gray-600">Cultural background and heritage</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-book-open-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Religion or Belief</h3>
                        <p className="text-sm text-gray-600">Religious or philosophical beliefs</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Sex</h3>
                        <p className="text-sm text-gray-600">Male, female or intersex</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-rainbow-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1 font-poppins">Sexual Orientation</h3>
                        <p className="text-sm text-gray-600">Lesbian, gay, bisexual or heterosexual</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Aims</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We are committed to creating an inclusive environment where everyone feels valued and respected. To achieve this, we aim to:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-hand-heart-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Dignity and Respect</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Treat all clients and learners with dignity and respect, regardless of their background or characteristics.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-door-open-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Accessible Services</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Provide accessible services wherever practicable, ensuring everyone can participate fully in our training and consultancy.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-graduation-cap-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Tailored Support</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Offer training and support tailored to ability and learning needs, recognising that everyone learns differently.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Partner Values</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Work with partners who adopt similar equality values and demonstrate commitment to diversity and inclusion.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-xl text-teal-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Challenge Discrimination</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Challenge unfair treatment or prejudicial behaviour whenever we encounter it, promoting a culture of respect.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Scope of Application</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This policy applies to all aspects of our business operations, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-graduation-cap-line text-sm text-teal-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Training Delivery</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All training courses and educational services we provide</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-sm text-teal-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Consultancy Work</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All consultancy and advisory services we deliver</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-settings-line text-sm text-teal-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Subcontracted Services</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All subcontractors and partners we work with</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-customer-service-line text-sm text-teal-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Client Interactions</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All communications and relationships with clients</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">What We Mean by Discrimination</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Discrimination can take many forms. We are committed to preventing and addressing:
                </p>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Direct Discrimination</h4>
                    <p className="text-sm text-gray-600">Treating someone less favourably because of a protected characteristic</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Indirect Discrimination</h4>
                    <p className="text-sm text-gray-600">Applying a rule or policy that disadvantages people with a protected characteristic</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Harassment</h4>
                    <p className="text-sm text-gray-600">Unwanted conduct that violates dignity or creates an intimidating, hostile or offensive environment</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Victimisation</h4>
                    <p className="text-sm text-gray-600">Treating someone unfairly because they have made or supported a complaint about discrimination</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Reasonable Adjustments</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We are committed to making reasonable adjustments to ensure our services are accessible to everyone. This may include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Adapting training materials and delivery methods</li>
                  <li>Providing additional support or resources</li>
                  <li>Adjusting assessment methods where appropriate</li>
                  <li>Ensuring accessible venues and facilities</li>
                  <li>Allowing extra time or breaks during training</li>
                  <li>Providing information in alternative formats</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  We encourage clients and learners to discuss their needs with us so we can make appropriate arrangements.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Reporting Concerns</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you experience or witness discrimination, harassment or victimisation in connection with our services, we encourage you to report it immediately.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    All reports will be treated seriously, investigated thoroughly and handled with sensitivity and confidentiality.
                  </p>
                  <div className="bg-white rounded-lg p-6 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">How to Report</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      If you have concerns about discrimination or unfair treatment, please contact us:
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700">
                        <strong>Email:</strong> <a href="mailto:info@jl-security.co.uk" className="text-teal-600 hover:text-teal-700 font-medium">info@jl-security.co.uk</a>
                      </p>
                      <p className="text-gray-700">
                        <strong>Phone:</strong> <a href="tel:+447834561015" className="text-teal-600 hover:text-teal-700 font-medium">+44 7834 561015</a>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">External Support Resources</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Equality and Human Rights Commission (EHRC)</h4>
                      <p className="text-gray-700 mb-1">Advice and guidance on equality and discrimination law</p>
                      <p className="text-gray-700">Phone: <a href="tel:08088004000" className="text-teal-600 hover:text-teal-700 font-medium">0808 800 4000</a></p>
                      <p className="text-gray-700">Website: <a href="https://www.equalityhumanrights.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">www.equalityhumanrights.com</a></p>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Citizens Advice</h4>
                      <p className="text-gray-700 mb-1">Free, confidential advice on discrimination issues</p>
                      <p className="text-gray-700">Phone: <a href="tel:08001448848" className="text-teal-600 hover:text-teal-700 font-medium">0800 144 8848</a></p>
                      <p className="text-gray-700">Website: <a href="https://www.citizensadvice.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">www.citizensadvice.org.uk</a></p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">ACAS (Advisory, Conciliation and Arbitration Service)</h4>
                      <p className="text-gray-700 mb-1">Workplace discrimination advice</p>
                      <p className="text-gray-700">Phone: <a href="tel:03001231100" className="text-teal-600 hover:text-teal-700 font-medium">0300 123 1100</a></p>
                      <p className="text-gray-700">Website: <a href="https://www.acas.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">www.acas.org.uk</a></p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Training &amp; Awareness</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We provide appropriate training to our staff and partners to ensure they:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Understand their responsibilities under equality legislation</li>
                  <li>Recognise and challenge discrimination and bias</li>
                  <li>Create inclusive environments for all learners</li>
                  <li>Make reasonable adjustments where needed</li>
                  <li>Respond appropriately to complaints or concerns</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Consequences of Non-Compliance</h2>
                <div className="bg-red-50 border-l-4 border-red-600 p-6">
                  <p className="text-gray-900 font-semibold mb-2">
                    <i className="ri-alert-line text-red-600 mr-2"></i>
                    Serious Action
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Any breach of this policy will be taken seriously and may result in <strong>disciplinary action</strong>, <strong>termination of contracts</strong>, or <strong>removal from our supply chain</strong>.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Policy Review</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This policy is reviewed annually or following any significant incident or change in legislation. We are committed to continuous improvement in our equality, diversity and inclusion practices.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Equality, Diversity &amp; Inclusion Policy or wish to report a concern, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>LEWIS SECURITY, RISK &amp; COMPLIANCE SOLUTIONS</strong></p>
                  <p className="text-gray-700 mb-2">Email: <a href="mailto:info@jl-security.co.uk" className="text-teal-600 hover:text-teal-700 font-medium">info@jl-security.co.uk</a></p>
                  <p className="text-gray-700">Phone: <a href="tel:+447834561015" className="text-teal-600 hover:text-teal-700 font-medium">+44 7834 561015</a></p>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
